import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PHOTOS = [
  {
    src: "/ap_real_mitch.jpeg",
    caption: "Golden hour on the reo",
    alt: "Boom pump truck and mixer over a rebar foundation at golden hour",
  },
  {
    src: "/ap_real_02.jpeg",
    caption: "Pour and level",
    alt: "Workers pouring and levelling concrete with the pump boom overhead",
  },
  {
    src: "/ap_real_03.jpeg",
    caption: "Big foundation day",
    alt: "Concrete pump pouring a large foundation with machinery on site",
  },
  {
    src: "/ap_real_04.jpeg",
    caption: "Site prep done right",
    alt: "Trenches and foundations prepared with machinery on site",
  },
  {
    src: "/ap_real_05.jpeg",
    caption: "Boom at work",
    alt: "Concrete pump boom working over the site",
  },
  {
    src: "/ap_real_06.jpeg",
    caption: "Crew on the ground",
    alt: "Crew working the concrete pour on site",
  },
  {
    src: "/ap_real_07.jpeg",
    caption: "Down the line",
    alt: "Concrete pumping line work on site",
  },
];

export default function Photos() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrent(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <div className="pt-16">
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mb-12 flex flex-wrap items-end justify-between gap-6 sm:mb-16"
          >
            <div>
              <p className="mb-4 font-tech text-xs uppercase tracking-[0.3em] text-[#4D8DFF]">
                Photos
              </p>
              <h1
                data-testid="photos-heading"
                className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight sm:text-6xl"
              >
                Here's some of
                <span className="text-[#0052FF]"> our work.</span>
              </h1>
            </div>
            <p className="max-w-xs text-sm text-slate-400 sm:text-base">
              Click the photo or swipe — straight off the sites we pump every
              week.
            </p>
          </motion.div>

          <div className="relative" data-testid="photos-carousel">
            <div
              ref={emblaRef}
              className="overflow-hidden border border-white/10"
            >
              <div className="flex">
                {PHOTOS.map((p, i) => (
                  <div
                    key={p.src}
                    className="relative min-w-0 flex-[0_0_100%]"
                  >
                    <img
                      src={p.src}
                      alt={p.alt}
                      data-testid={`photos-slide-${i}`}
                      onClick={next}
                      className="aspect-[16/10] w-full cursor-pointer object-cover"
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-[#0A0C10]/90 to-transparent p-5">
                      <span
                        data-testid={`photos-caption-${i}`}
                        className="font-tech text-xs uppercase tracking-[0.25em] text-white sm:text-sm"
                      >
                        {p.caption}
                      </span>
                      <span className="font-tech text-xs tracking-widest text-[#7EB0FF]">
                        {String(i + 1).padStart(2, "0")} /{" "}
                        {String(PHOTOS.length).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={prev}
              data-testid="photos-prev"
              aria-label="Previous photo"
              className="absolute left-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-[#0A0C10]/80 text-white backdrop-blur-sm transition-all duration-300 hover:bg-[#0052FF]"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={next}
              data-testid="photos-next"
              aria-label="Next photo"
              className="absolute right-4 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-[#0A0C10]/80 text-white backdrop-blur-sm transition-all duration-300 hover:bg-[#0052FF]"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 flex justify-center gap-2">
            {PHOTOS.map((_, i) => (
              <button
                key={i}
                onClick={() => emblaApi?.scrollTo(i)}
                data-testid={`photos-dot-${i}`}
                aria-label={`Go to photo ${i + 1}`}
                className={`h-2.5 transition-all duration-300 ${
                  i === current
                    ? "w-8 bg-[#0052FF]"
                    : "w-2.5 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}